// Ghi/merge Avatar block (.claude/, AGENTS.md, CLAUDE.md, .gitnexus/) vào
// <git-common-dir>/info/exclude cho 1 repo — thay vì .gitignore (tracked, gây
// dirty vĩnh viễn). Untracked, không vào lịch sử git, không hiện trong
// `git status`. Ticket #138 phase-02.
import { copyFile, readFile } from "node:fs/promises";
import { basename, join } from "node:path";
import {
  mergeAvatarBlockIntoContent,
  stripAvatarBlockFromContent,
} from "./avatar-block-text-ops.js";
import { ensureDir, pathExists, writeTextAtomic } from "./filesystem-helpers.js";
import { resolveGitCommonDir } from "./resolve-git-common-dir.js";

// Ném khi repoPath không phải git repo (resolveGitCommonDir trả null). Class
// riêng (không string-match message) để call site phân loại đúng bucket.
export class NotAGitRepoError extends Error {
  constructor(public readonly repoPath: string) {
    super(`"${repoPath}" không phải git repo (hoặc thư mục không tồn tại) — bỏ qua ghi exclude.`);
    this.name = "NotAGitRepoError";
  }
}

// Ném khi info/exclude có marker Avatar hỏng (START mà thiếu/đảo END) — file bị
// sửa tay. KHÔNG tự sửa: đụng vào phần rule member tự viết sau marker hỏng có
// thể xoá mất rule thật của họ.
export class CorruptedExcludeMarkerError extends Error {
  constructor(public readonly repoPath: string) {
    super(
      `"${repoPath}": info/exclude có marker Avatar hỏng (START không có END, hoặc END đứng trước START) — KHÔNG tự sửa, cần xử tay.`,
    );
    this.name = "CorruptedExcludeMarkerError";
  }
}

export interface WriteAvatarBlockToGitExcludeOptions {
  // BẮT BUỘC — không optional, không có mặc định ngầm (hàm không nhận
  // workspaceRoot nên không tự tính được). Call site phải tự truyền
  // (vd join(workspaceRoot, ".claude", "_backup")).
  backupDir: string;
}

// Trả về đường dẫn file backup vừa tạo, hoặc `null` nếu KHÔNG backup gì (ca
// no-op — nội dung đã đúng sẵn — hoặc file exclude chưa tồn tại trước đó nên
// không có gì để backup). Throw NotAGitRepoError / CorruptedExcludeMarkerError
// / lỗi fs khác — call site bắt và phân bucket (patch-all-repos-git-exclude.ts).
export async function writeAvatarBlockToGitExclude(
  repoPath: string,
  block: string,
  opts: WriteAvatarBlockToGitExcludeOptions,
): Promise<string | null> {
  const common = resolveGitCommonDir(repoPath);
  if (common === null) {
    throw new NotAGitRepoError(repoPath);
  }

  const infoDir = join(common, "info");
  await ensureDir(infoDir);
  const excludePath = join(infoDir, "exclude");

  const fileExisted = await pathExists(excludePath);
  const existing = fileExisted ? await readFile(excludePath, "utf8") : "";

  // Gate corrupted TRƯỚC MỌI thao tác ghi — dùng lại phân loại của
  // stripAvatarBlockFromContent (1 nguồn phân loại marker, DRY với phase-01).
  if (stripAvatarBlockFromContent(existing).status === "corrupted") {
    throw new CorruptedExcludeMarkerError(repoPath);
  }

  // Gate no-op TRƯỚC backup: nội dung không đổi → không backup, không ghi,
  // không tạo tmp file. Call site vẫn coi ca này là thành công (patched) —
  // exclude đã chắc chắn đúng, kể cả không ghi gì trong lần chạy này.
  const merged = mergeAvatarBlockIntoContent(existing, block);
  if (merged === existing) return null;

  // Backup TRƯỚC khi ghi — CHỈ khi file cũ tồn tại (không có gì để backup nếu
  // file chưa tồn tại). Copy fail → throw lan ra call site, KHÔNG ghi gì —
  // file gốc (nếu có) còn nguyên.
  let backupPath: string | null = null;
  if (fileExisted) {
    await ensureDir(opts.backupDir);
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    backupPath = join(opts.backupDir, `exclude-${basename(repoPath)}-${ts}`);
    await copyFile(excludePath, backupPath);
  }

  await writeTextAtomic(excludePath, merged);
  return backupPath;
}
